import * as d3 from 'd3';
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as setup from 'setup';
import Chart from '../Chart/Chart';

const axes = {
    labelX: 'Hands',
    labelY: '$',
    prefixY: '$',
    suffixY: '',
};

const bandProps = [
    { color: setup.colors.green, label: 'Most likely' },
    { color: setup.colors.yellow, label: 'Less likely' },
    { color: setup.colors.orange, label: 'Unlikely' },
    { color: setup.colors.red, label: 'Highly unlikely' },
];

const maxX = Math.max(
    setup.charts.allIn.histogram.maxHands,
    setup.charts.meteredFunding.histogram.maxHands,
    setup.charts.spyOpponent.histogram.maxHands
);

const maxY = [
    setup.charts.allIn.hurricane.highlyUnlikely,
    setup.charts.meteredFunding.hurricane.highlyUnlikely,
    setup.charts.spyOpponent.hurricane.highlyUnlikely,
].reduce((max, band) => {
    const bandMax = _.max(band.map(point => point[0])) || 0;
    return Math.max(bandMax, max);
}, 0);

export default function HurricaneChart({ strategy }: HurricaneChartProps) {
    const [chartRect, setChartRect] = useState<DOMRect>(new DOMRect());
    const [container, setContainer] = useState(d3.select(null));

    const containerRef = useCallback((ref: any) => {
        const newContainer = d3.select(ref);
        setContainer(newContainer);
    }, []);

    const bands = useMemo(() => {
        const { hurricane } = setup.charts[strategy];
        return [hurricane.mostLikely, hurricane.lessLikely, hurricane.unlikely, hurricane.highlyUnlikely].map(
            (values, index) => ({
                ...bandProps[index],
                values,
            })
        );
    }, [strategy]);

    const scaleX = useMemo(() => {
        return d3
            .scaleLinear()
            .domain([0, maxX * 1.2])
            .range([0, chartRect.width]);
    }, [chartRect]);

    const scaleY = useMemo(() => {
        return d3
            .scaleLinear()
            .domain([0, maxY * 1.2])
            .range([chartRect.height, 0]);
    }, [chartRect]);

    useEffect(() => {
        // Empty container
        container.selectChildren().remove();

        // Check bars
        if (!bands.length || !chartRect.width) {
            return;
        }

        // Create area function
        const getArea = d3
            .area()
            .curve(d3.curveCardinal.tension(0.2))
            .x((_, i) => scaleX(i))
            .y1(d => scaleY(d[0]))
            .y0(d => scaleY(d[1]));

        // Add bands
        container
            .selectAll('path')
            .data(bands.slice().reverse())
            .enter()
            .append('path')
            .attr('d', band => getArea(band.values))
            .attr('fill', band => band.color)
            .attr('data-tooltip-id', 'chart-tooltip')
            .attr('data-tooltip-html', d => d.label);
    }, [bands, chartRect, container, scaleX, scaleY]);

    return (
        <Chart
            axes={axes}
            legend={bandProps}
            scaleX={scaleX}
            scaleY={scaleY}
            title={`Results from ${setup.charts[strategy].label} strategy`}
            onRectChange={setChartRect}
        >
            <g ref={containerRef} />
        </Chart>
    );
}
